import { MainHeader, Section, SectionParagraph } from '@/components/shared/Content';
import { API_PREFIX_LEGACY } from '@/const/api';
import errorIconImage from '@/static/assets/attention_mark_white.png';
import loveImage from '@/static/assets/lemonade_goes_global/love.png';
import reportImage from '@/static/assets/transparency/transparency-report-image.svg';
import { DialogProvider, Flex, useDialogContext } from '@lemonade-hq/cdk';
import { BaseDialog, Button, DialogBody, DialogFooter, DialogHeader, InputText } from '@lemonade-hq/nuis';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import { Client, useAnalyticsContext } from '@lemonade-hq/nuiza';
import { isValidEmail } from '@lemonade-hq/ts-helpers';
import parse from 'html-react-parser';
import Image from 'next/image';
import type React from 'react';
import { useCallback, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const StyledInput = styled(InputText)`
  width: 100%;
  input {
    height: 100%;
  }
`;

const StyledForm = styled.form`
  display: flex;
  position: relative;
  gap: 10px;
  width: 100%;
  max-width: 500px;
  justify-content: center;
  align-items: flex-stretch;

  ${MEDIA_QUERIES.mobile`
    flex-direction: column;
  `}
`;

const pulseAnimation = keyframes`
    0% { transform: scale(0.8); }
    100% { transform: scale(1.1); }
`;

const LoveImage = styled(Image)`
  will-change: transform;
  margin-bottom: 32px;
  animation: ${pulseAnimation} 0.8s ease-in-out alternate-reverse infinite;
`;

const ErrorWrapper = styled(Flex)`
  position: absolute;
  top: -30px;
  left: 0;
  gap: 6px;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.pink};

  ${SectionParagraph} {
    font-size: 14px;
  }
`;

export interface HeroProps {
  title: string;
  subtitle: string;
}

export const SuccessDialog = () => {
  return (
    <BaseDialog>
      <DialogHeader>
        <LoveImage src={loveImage} alt="love" />
      </DialogHeader>
      <DialogBody>
        <div style={{ paddingInline: '30px' }}>
          <MainHeader>{`You're in!`}</MainHeader>
        </div>
      </DialogBody>
      <DialogFooter>
        <SectionParagraph>Thanks for joining.</SectionParagraph>
      </DialogFooter>
    </BaseDialog>
  );
};

const ERROR_MESSAGE = {
  error: 'Please enter a valid email',
  serverError: 'Something went wrong, please try again later',
};

const Form: React.FC = () => {
  const { trackEvent, identify } = useAnalyticsContext();
  const { openDialog } = useDialogContext();
  const [state, setState] = useState<{
    email: string;
    status: 'idle' | 'loading' | 'success' | 'error' | 'serverError';
  }>({
    email: '',
    status: 'idle',
  });

  const updateState = (subState: Partial<typeof state>): void => {
    setState(formState => {
      return {
        ...formState,
        ...subState,
      };
    });
  };

  const onOpenDialog = useCallback(() => {
    openDialog(<SuccessDialog />);
  }, [openDialog]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    updateState({ status: 'loading' });

    if (!isValidEmail(state.email)) {
      updateState({ status: 'error' });
      return;
    }

    try {
      await Client.post(`${API_PREFIX_LEGACY}/subscribe`, {
        email: state.email,
        origin: 'transparency_page',
      });
      updateState({ status: 'success' });
      identify(state.email, { email: state.email });
      trackEvent('transparency_page_register_button_clicked', {
        email: state.email,
      });
      onOpenDialog();
    } catch (e: unknown) {
      console.log(e);
      updateState({ status: 'serverError' });
    }
  };

  return (
    <>
      <StyledForm onSubmit={onSubmit}>
        {(state.status === 'error' || state.status === 'serverError') && (
          <ErrorWrapper>
            <Image src={errorIconImage} alt="" height={16} width={16} />
            <SectionParagraph>{ERROR_MESSAGE[state.status]}</SectionParagraph>
          </ErrorWrapper>
        )}
        <StyledInput
          placeholder="my email"
          onChange={e => {
            updateState({ email: e.target.value, status: 'idle' });
          }}
          value={state.email}
          disabled={state.status === 'success'}
        />
        <Button
          flavor="primary"
          minWidth={160}
          centerLoader
          isLoading={state.status === 'loading'}
          glowing
          disabled={state.status === 'success'}
        >
          Join
        </Button>
      </StyledForm>
    </>
  );
};

export const Hero: React.FC<HeroProps> = ({ title, subtitle }) => {
  return (
    <Section py={[40, 70]} gap="56px">
      <Flex gap="20px" flexDirection="column">
        <MainHeader>{parse(title)}</MainHeader>
        <SectionParagraph>{parse(subtitle)}</SectionParagraph>
      </Flex>
      <DialogProvider>
        <Form />
      </DialogProvider>
      <Image src={reportImage} alt="report image" />
    </Section>
  );
};
